import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';
import { api } from '../lib/api';

/**
 * CityAutocomplete — typeahead over Colombia's 1,122 DIVIPOLA municipalities.
 *
 * Backed by GET /api/locations/municipalities?q=... with 200ms debounce.
 * Selecting a result emits a full payload: { name, dept, lat, lng, code }.
 *
 * Props:
 *   value      → currently selected city name (string)
 *   onChange   → (cityName) => void  (called for backwards-compat, raw string)
 *   onSelect   → (locationObject) => void  (full payload; preferred for new code)
 *   placeholder, autoFocus, className → cosmetics
 */
export default function CityAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Busca un municipio (ej. Zipaquirá, Chía, Soacha...)',
  autoFocus = false,
  className = '',
  testId = 'city-autocomplete',
}) {
  const [q, setQ] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQ(value || ''); }, [value]);

  // Click outside closes the popover
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.listMunicipalities(q, 10);
        setItems(res.items || []);
        setHi(0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  const pick = (m) => {
    setQ(m.name);
    setOpen(false);
    if (onChange) onChange(m.name);
    if (onSelect) onSelect(m);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && items[hi]) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (onChange) onChange(e.target.value); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          autoFocus={autoFocus}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={`${testId}-input`}
          className="w-full h-12 pl-9 pr-9 rounded-md border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-habita"
        />
        {loading && <Loader2 className="w-4 h-4 text-habita animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>

      {open && items.length > 0 && (
        <ul
          data-testid={`${testId}-list`}
          className="absolute z-50 mt-1.5 w-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden max-h-80 overflow-y-auto"
        >
          {items.map((m, i) => (
            <li
              key={m.code || `${m.name}-${m.dept}`}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(m)}
              data-testid={`${testId}-option-${i}`}
              className={`flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer text-sm ${i === hi ? 'bg-habita-mint' : 'hover:bg-gray-50'}`}
            >
              <MapPin className="w-4 h-4 text-habita shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-habita-dark">{m.name}</span>
                <span className="text-xs text-gray-500 ml-2">{m.dept}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && items.length === 0 && q && (
        <div className="absolute z-50 mt-1.5 w-full bg-white rounded-xl border border-gray-200 shadow-lg px-3.5 py-3 text-sm text-gray-500">
          Sin resultados para &quot;{q}&quot;. Prueba con otro nombre.
        </div>
      )}
    </div>
  );
}
